import { Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerFooter, DrawerHeader, DrawerOverlay, HStack, VStack, useToast } from '@chakra-ui/react';
import { InputFormControl } from '@jurnee/common/src/components/InputFormControl';
import { Loader } from '@jurnee/common/src/components/Loader';
import { MenuItemWrapper } from '@jurnee/common/src/components/MenuItemWrapper';
import { PrimaryButton } from '@jurnee/common/src/components/primitives/buttons/PrimaryButton';
import { SecondaryButton } from '@jurnee/common/src/components/primitives/buttons/SecondaryButton';
import { PartnerUserJSON } from '@jurnee/common/src/entities/PartnerUser';
import { getErrorToast, getSuccessToast } from '@jurnee/common/src/utils/toasts';
import { updateUser } from 'api/user';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store';
import { getIsPartnerUserSelector } from 'store/auth/auth.selectors';
import { getUser } from 'store/user/user.thunks';

export function UserSettingsDrawer() {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const isPartnerUser = useSelector(getIsPartnerUserSelector);
  const { t } = useTranslation(['navbar', 'common', 'toasts']);;

  const [user, setUser] = useState<PartnerUserJSON | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const isValid = Boolean(user?.firstName && user?.lastName && user?.email);

  function onClose() {
    setIsOpen(false);
  }

  async function fetchUser() {
    try {
      const user = await dispatch(getUser()).unwrap();
      setUser(user);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      toast(getErrorToast(t('toasts:userFetch.error'), message));
    } finally {
      setIsLoading(false);
    }
  }

  function onOpen() {
    setIsOpen(true);
    fetchUser();
  }

  async function onSave() {
    if (!user) {
      return;
    }
    try {
      setIsSaving(true);

      const updatedUser = await updateUser(user.id, {
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email ?? '',
        phoneNumber: user.phoneNumber ? user.phoneNumber : null
      });

      setUser(updatedUser);
      toast(getSuccessToast(t('toasts:userUpdate.success')));
      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      toast(getErrorToast(t('toasts:userUpdate.error'), message));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <MenuItemWrapper icon='user' label={t('userCard.settings')} onClick={onOpen} isDisabled={!isPartnerUser} />
      <Drawer isOpen={isOpen} onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader>
            {t('common:settings')}
            <DrawerCloseButton />
          </DrawerHeader>
          <DrawerBody p={5}>
            {isLoading ? (
              <Loader />
            ) : (
              <VStack spacing={5}>
                <InputFormControl name="firstName" value={user?.firstName ?? null} onChange={(value) => user && setUser({ ...user, firstName: value.trim() })} isRequired />
                <InputFormControl name="lastName" value={user?.lastName ?? null} onChange={(value) => user && setUser({ ...user, lastName: value.trim() })} isRequired />
                <InputFormControl name="email" value={user?.email ?? null} type="email" onChange={(value) => user && setUser({ ...user, email: value.trim() })} isRequired />
                <InputFormControl name="phoneNumber" value={user?.phoneNumber ?? null} type="email" onChange={(value) => user && setUser({ ...user, phoneNumber: value.trim() })} hasHelper />
              </VStack>
            )}
          </DrawerBody>
          <DrawerFooter>
            <HStack justifyContent="space-between" w="100%">
              <SecondaryButton size="sm" colorScheme="pink" onClick={onClose}>{t('common:buttons.close')}</SecondaryButton>
              <PrimaryButton isLoading={isSaving} size="sm" colorScheme="teal" onClick={onSave} isDisabled={!isValid}>{t('common:buttons.save')}</PrimaryButton>
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}